import { BaseLevel, Level, LevelWithExtraFiltersAndIDL } from './types/levels';
import { nlwTierEstimates } from './types/nlw';

export function getFullLevelThumbnail(levelId: number) {
    return `https://raw.githubusercontent.com/All-Rated-Extreme-Demon-List/Thumbnails/main/levels/full/${levelId}.webp`;
}

export function getCardLevelThumbnail(levelId: number) {
    return `https://raw.githubusercontent.com/All-Rated-Extreme-Demon-List/Thumbnails/main/levels/cards/${levelId}.webp`;
}

export const getLevelDisplayName = (level: BaseLevel) =>
    `${getLevelDisplayPrefix(level)}${level.name}`;

export const getLevelDisplayPrefix = (level: BaseLevel) =>
    'status' in level
        ? level.status !== 'MainList'
            ? `${level.status} - `
            : level.position != null
              ? `#${level.position} `
              : ''
        : '';

export function getPackThumbnail(packid: string) {
    return `https://raw.githubusercontent.com/All-Rated-Extreme-Demon-List/Thumbnails/main/packs/${packid}.webp`;
}

export const formatLevelGDID = (level: BaseLevel) =>
    `${level.level_id}${level.two_player ? '_2p' : ''}`;

type ComparableLevel = BaseLevel | Level | LevelWithExtraFiltersAndIDL;

export const compareLevelRank = (a: ComparableLevel, b: ComparableLevel) => {
    const isPlacedA = a.position !== null;
    const isPlacedB = b.position !== null;

    if (isPlacedA != isPlacedB) {
        // first sort all placed levels (MainList/Legacy) above unplaced ones (Pending/Removed)
        return isPlacedA ? -1 : 1;
    } else if (!isPlacedA && !isPlacedB) {
        // for unplaced ones sort by index of tier/estimated tier in nlw order, then by name
        const tierA =
            'nlw_tier' in a
                ? (a.nlw_tier ?? a.nlw_tier_estimate ?? null)
                : null;
        const tierB =
            'nlw_tier' in b
                ? (b.nlw_tier ?? b.nlw_tier_estimate ?? null)
                : null;

        const tierIndexA =
            tierA !== null ? nlwTierEstimates.indexOf(tierA) : Infinity;
        const tierIndexB =
            tierB !== null ? nlwTierEstimates.indexOf(tierB) : Infinity;

        if (tierIndexA !== tierIndexB) {
            return tierIndexA - tierIndexB;
        }

        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    } else {
        // then sort placed ones by position, MainList above Legacy
        const isOnIDLA = 'idl_position' in a && a.idl_position !== -1;
        const isOnIDLB = 'idl_position' in b && b.idl_position !== -1;

        if (
            // for legacy levels, sort by IDL pos if available or by regular pos otherwise
            a.status == 'Legacy' &&
            b.status == 'Legacy' &&
            (isOnIDLA || isOnIDLB)
        ) {
            if (isOnIDLA != isOnIDLB) {
                return isOnIDLA ? -1 : 1;
            } else if (isOnIDLA && isOnIDLB) {
                return a.idl_position - b.idl_position;
            } else {
                return a.position! - b.position!;
            }
        } else {
            return a.position! - b.position!;
        }
    }
};

export const compareLevelEnjoyment = (
    a: ComparableLevel,
    b: ComparableLevel,
) => {
    // for enjoyment, sort levels by edel value and those without one below
    const isOnEDELA = 'edel_enjoyment' in a && !!a.edel_enjoyment;
    const isOnEDELB = 'edel_enjoyment' in b && !!b.edel_enjoyment;

    if (isOnEDELA !== isOnEDELB) {
        return isOnEDELA ? -1 : 1;
    } else if (isOnEDELA && isOnEDELB) {
        return b.edel_enjoyment! - a.edel_enjoyment!;
    }
    return 0;
};

export function compareLevels(
    a: ComparableLevel,
    b: ComparableLevel,
    sort: 'rank' | 'enjoyment' | 'records' | 'creation_date' = 'rank',
    reverse: boolean = false,
) {
    let result = 0;
    switch (sort) {
        case 'rank':
            result = compareLevelRank(a, b);
            break;
        case 'enjoyment':
            result = compareLevelEnjoyment(a, b);
            break;
        case 'records':
            result =
                ('records' in b ? (b.records ?? 0) : 0) -
                ('records' in a ? (a.records ?? 0) : 0);
            break;
        case 'creation_date':
            result = b.level_id - a.level_id;
            break;
    }
    return reverse ? -result : result;
}
